import React, {useEffect, useState} from 'react';
import moment from 'moment';
import Util from '../forecast-app/shared/util/util';
import UpdateCustomFieldValueMutation from '../mutations/update_custom_field_value_mutation';
import {BasicNumberInput, BasicTextInput} from '@/web-components';
import {ComboBox, Text} from '@forecast-it/design-system';
import {CUSTOM_FIELD_TYPE, CustomFieldType, DATE_PICKER_STYLE} from '../constants';
import {CustomFieldBooleanInput} from './CustomFieldBooleanInput';
import {CustomFieldCurrencyInput} from './CustomFieldCurrencyInput';
import {CustomFieldHyperlinkInput} from './CustomFieldHyperlinkInput';
import {CustomFieldMultiSelectInput} from './CustomFieldMultiSelectInput';
import {DesignSystemComboBoxBorderAdapter} from '../forecast-app/shared/components/dropdowns/DesignSystemComboBoxOldLookAdapter';
import DateRangePicker from '../forecast-app/shared/components/date-picker/date_picker_v3';
import {
	decimalSeparatorFor,
	filterIntegerInput,
	integerConverterValue,
	isValidDecimal,
	isValidInteger,
	makeDecimalConverter,
	makeDecimalFilter,
	dropdownOptionKey,
} from '../forecast-app/shared/util/customFieldValueValidation';
type Option = {
	id: string;
	displayName: string;
};

type CustomFieldValueInputProps = {
	customFieldKey: unknown;
	displayName: unknown;
	value: string | null;
	entityType: unknown;
	entityId: unknown;
	type: CustomFieldType;
	options?: Option[];
	readOnly?: boolean;
	language?: string;
	// Show a Yes/No field's name beside its checkbox, on a surface that does not print it above the field.
	showBooleanLabel?: boolean;
	// Edit a Hyperlink with the box alone, without Save and Cancel, for a narrow table cell.
	hyperlinkBoxOnly?: boolean;
	// A Currency field's ISO 4217 code, from its value node.
	currencyCode?: string | null;
	// Open a Multi-select's checkbox list in a portal, for a table cell that clips what overflows it. Never
	// inside a modal, which a portalled list would open behind.
	multiSelectPortalled?: boolean;
};

const GRAPHQL_TYPE_MAP: Record<CustomFieldType, string> = {
	[CUSTOM_FIELD_TYPE.STRING]: 'TextCustomFieldValue',
	[CUSTOM_FIELD_TYPE.DROPDOWN]: 'DropdownCustomFieldValue',
	[CUSTOM_FIELD_TYPE.INTEGER]: 'IntegerCustomFieldValue',
	[CUSTOM_FIELD_TYPE.DECIMAL]: 'DecimalCustomFieldValue',
	[CUSTOM_FIELD_TYPE.DATE]: 'DateCustomFieldValue',
	[CUSTOM_FIELD_TYPE.BOOLEAN]: 'BooleanCustomFieldValue',
	[CUSTOM_FIELD_TYPE.HYPERLINK]: 'HyperlinkCustomFieldValue',
	[CUSTOM_FIELD_TYPE.CURRENCY]: 'CurrencyCustomFieldValue',
	[CUSTOM_FIELD_TYPE.MULTI_SELECT]: 'MultiSelectCustomFieldValue',
};

export function CustomFieldValueInput({
	customFieldKey,
	displayName,
	value,
	entityType,
	entityId,
	type,
	options = [],
	readOnly,
	language,
	showBooleanLabel,
	hyperlinkBoxOnly,
	currencyCode,
	multiSelectPortalled,
}: CustomFieldValueInputProps): React.JSX.Element {
	const decSep = decimalSeparatorFor(language);

	// Convert stored '.' to locale separator. For decimal fields, also ensures one decimal place.
	const toDisplay = (val: string | null) => {
		if (val === null || val === '') return val;
		return decSep === ',' ? val.replace('.', ',') : val;
	};

	const toDecimalDisplay = (val: string | null) => {
		const withSep = toDisplay(val);
		if (withSep === null || withSep === '') return withSep;
		return withSep.includes(decSep) ? withSep : withSep + decSep + '0';
	};

	const toDisplayForType = type === CUSTOM_FIELD_TYPE.DECIMAL ? toDecimalDisplay : (val: string | null) => val;

	const [currentValue, setCurrentValue] = useState(toDisplayForType(value));

	useEffect(() => {
		setCurrentValue(toDisplayForType(value));
	}, [value, decSep]);

	const handleCustomFieldChange = (newValue: string | null) => {
		Util.CommitMutation(UpdateCustomFieldValueMutation, {
			__typename: GRAPHQL_TYPE_MAP[type],
			entityId,
			entityType,
			key: customFieldKey,
			value: newValue,
		});
	};

	const handleSave = () => {
		if (value !== currentValue) {
			if (type === CUSTOM_FIELD_TYPE.INTEGER && !isValidInteger(currentValue)) {
				return;
			}
			handleCustomFieldChange(currentValue);
		}
	};

	const onComboBoxChange = (optionId: string) => {
		setCurrentValue(optionId);
		handleCustomFieldChange(optionId);
	};

	if (type === CUSTOM_FIELD_TYPE.DROPDOWN) {
		return (
			<DesignSystemComboBoxBorderAdapter>
				<ComboBox
					data-cy={'custom-field-dropdown'}
					aria-label={displayName + ' dropdown field'}
					defaultItems={options}
					value={currentValue}
					onChange={(optionId: string) => onComboBoxChange(optionId)}
					width={ComboBox.WIDTH.FLEX}
					menuTrigger="focus"
					size={'small'}
				>
					{option => (
						<ComboBox.Item key={dropdownOptionKey(option.id)} textValue={option.displayName}>
							<Text data-cy={'option-' + option.displayName} ellipsis={true} tooltipOnEllipsis={true} size={'2'}>
								{option.displayName}
							</Text>
						</ComboBox.Item>
					)}
				</ComboBox>
			</DesignSystemComboBoxBorderAdapter>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.INTEGER) {
		return (
			<BasicNumberInput
				value={currentValue ?? ''}
				converterValue={integerConverterValue}
				filterInput={filterIntegerInput}
				onChange={setCurrentValue}
				callback={(floatValue: number | null, e: unknown, isDirty: boolean) => {
					if (!isDirty) return;
					const inputValue = (e as React.FocusEvent<HTMLInputElement>).target.value;
					if (inputValue === '') {
						handleCustomFieldChange(null);
					} else if (floatValue != null && isValidInteger(String(floatValue))) {
						handleCustomFieldChange(String(floatValue));
					} else {
						setCurrentValue(value);
					}
				}}
				disabled={readOnly}
				cy={'custom-field-integer-input'}
			/>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.DECIMAL) {
		return (
			<BasicNumberInput
				value={currentValue ?? ''}
				converterValue={makeDecimalConverter()}
				filterInput={makeDecimalFilter(decSep)}
				onChange={setCurrentValue}
				callback={(floatValue: number | null, e: unknown, isDirty: boolean) => {
					if (!isDirty) return;
					const inputValue = (e as React.FocusEvent<HTMLInputElement>).target.value;
					if (inputValue === '') {
						handleCustomFieldChange(null);
					} else if (floatValue != null && isValidDecimal(inputValue)) {
						handleCustomFieldChange(inputValue.replace(',', '.'));
					} else {
						setCurrentValue(toDecimalDisplay(value));
					}
				}}
				disabled={readOnly}
				cy={'custom-field-decimal-input'}
			/>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.DATE) {
		const momentValue = currentValue ? moment(currentValue, 'YYYY-MM-DD', true) : null;
		return (
			<DateRangePicker
				isSingleDatePicker={true}
				startDate={momentValue?.isValid() ? momentValue : null}
				handleDateRangeChange={(date: moment.Moment) => {
					const isoDate = date ? date.format('YYYY-MM-DD') : null;
					setCurrentValue(isoDate);
					handleCustomFieldChange(isoDate);
				}}
				clearBothDates={() => {
					setCurrentValue(null);
					handleCustomFieldChange(null);
				}}
				datePickerStyle={DATE_PICKER_STYLE.SLIM}
				highlighted={true}
				clearable={true}
				disabled={readOnly}
				userpilot={'custom-field-date-picker'}
			/>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.BOOLEAN) {
		return (
			<CustomFieldBooleanInput
				value={currentValue}
				readOnly={readOnly}
				label={String(displayName)}
				showLabel={showBooleanLabel}
				onChange={next => {
					setCurrentValue(next);
					handleCustomFieldChange(next);
				}}
			/>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.CURRENCY) {
		return (
			<CustomFieldCurrencyInput
				value={currentValue}
				currencyCode={currencyCode}
				label={String(displayName)}
				readOnly={readOnly}
				onChange={next => {
					setCurrentValue(next);
					handleCustomFieldChange(next);
				}}
			/>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.MULTI_SELECT) {
		return (
			<CustomFieldMultiSelectInput
				value={currentValue}
				options={options}
				label={String(displayName)}
				disabled={readOnly}
				portalled={multiSelectPortalled}
				onCommit={next => {
					setCurrentValue(next);
					handleCustomFieldChange(next);
				}}
			/>
		);
	}

	if (type === CUSTOM_FIELD_TYPE.HYPERLINK) {
		return (
			<CustomFieldHyperlinkInput
				value={currentValue}
				readOnly={readOnly}
				label={String(displayName)}
				boxOnly={hyperlinkBoxOnly}
				onSave={next => {
					setCurrentValue(next);
					handleCustomFieldChange(next);
				}}
			/>
		);
	}

	return <BasicTextInput value={currentValue ?? ''} onChange={setCurrentValue} callback={handleSave} disabled={readOnly} />;
}
